export const APP_SHELL_HANDOFF_TIMEOUT_MS = 8000;
export const APP_SHELL_REMOVE_DELAY_MS = 200;

interface AppShellScheduler {
  requestAnimationFrame(callback: FrameRequestCallback): number;
  cancelAnimationFrame(handle: number): void;
  setTimeout(handler: TimerHandler, timeout?: number): number;
  clearTimeout(handle: number): void;
}

/**
 * Hands the inline startup shell to the first committed React frame.
 *
 * The shell and React fallback intentionally use the same flat neon ring. Wait
 * for React to put a real element in #app before starting the two-frame
 * handoff. Counting frames from createRoot().render() can reveal a black screen
 * on a resource-starved WebView whose React commit has not happened yet. The
 * timeout is a hard escape hatch so the startup shell cannot remain forever.
 */
export function scheduleAppShellHandoff(
  root: HTMLElement,
  scheduler: AppShellScheduler = window
): () => void {
  let faded = false;
  let firstFrameId: number | null = null;
  let secondFrameId: number | null = null;
  let removalTimeoutId: number | null = null;

  const fade = () => {
    if (faded) return;
    faded = true;

    if (firstFrameId !== null) {
      scheduler.cancelAnimationFrame(firstFrameId);
      firstFrameId = null;
    }
    if (secondFrameId !== null) {
      scheduler.cancelAnimationFrame(secondFrameId);
      secondFrameId = null;
    }
    scheduler.clearTimeout(hardTimeoutId);
    root.classList.add('app-shell-fade');

    const shell = root.querySelector<HTMLElement>('.app-shell');
    if (!shell) return;

    removalTimeoutId = scheduler.setTimeout(() => {
      shell.remove();
      removalTimeoutId = null;
    }, APP_SHELL_REMOVE_DELAY_MS);
  };

  const hardTimeoutId = scheduler.setTimeout(fade, APP_SHELL_HANDOFF_TIMEOUT_MS);

  const app = root.querySelector<HTMLElement>('#app');
  const waitForReactCommit = () => {
    firstFrameId = null;

    if (!app || app.childElementCount === 0) {
      firstFrameId = scheduler.requestAnimationFrame(waitForReactCommit);
      return;
    }

    secondFrameId = scheduler.requestAnimationFrame(() => {
      secondFrameId = null;
      fade();
    });
  };

  firstFrameId = scheduler.requestAnimationFrame(waitForReactCommit);

  return () => {
    if (firstFrameId !== null) scheduler.cancelAnimationFrame(firstFrameId);
    if (secondFrameId !== null) scheduler.cancelAnimationFrame(secondFrameId);
    scheduler.clearTimeout(hardTimeoutId);
    if (removalTimeoutId !== null) scheduler.clearTimeout(removalTimeoutId);
  };
}
